import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../auth';
import '../styles/Gatekeeper.css';

/**
 * Gatekeeper Component
 * Secure access point for the website
 * Users must provide name and email to access the site
 */
const Gatekeeper = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      const result = await registerUser(name, email);

      if (result.success) {
        setShowSuccess(true);
        
        // Send user details to PHP backend (non-blocking)
        fetch('/test.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: name,
            email: email,
          }),
        })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            console.log("✅ User details sent successfully!", data);
          } else {
            console.warn("⚠️ Email notification failed, but access granted:", data.message);
          }
        })
        .catch(err => console.warn('⚠️ Email notification error (access still granted):', err));

        // Reload page after 3 seconds to show the success message
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gatekeeper-container">
      <div className="gatekeeper-background"></div>
      
      <div className="gatekeeper-content">
        <div className="gatekeeper-card">
          {/* Logo/Branding */}
          <div className="gatekeeper-header">
            <h1 className="gatekeeper-title">incogniflex</h1>
            <p className="gatekeeper-subtitle">
              Where Human Intelligence Meets Artificial Brilliance
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="gatekeeper-form">
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={loading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Get Access'}
            </button>
          </form>

          {/* Footer Info */}
          <p className="gatekeeper-footer">
            By accessing incogniflex, you agree to our terms of service and privacy policy.
          </p>
        </div>
      </div>

      {/* Success Popup Modal */}
      {showSuccess && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>Your information has been received. We'll be in touch soon.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gatekeeper;
